import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useLiveSearch — debounced search hook that calls Sharetribe search API.
 *
 * Integrates with the Sharetribe SDK's listings.query() endpoint.
 * Results are keyed by query string for instant cache lookup.
 *
 * @param {Object} options
 * @param {number}   [options.debounceMs]  — debounce delay (default: 300)
 * @param {number}   [options.minLength]   — minimum query length (default: 2)
 * @param {number}   [options.maxResults]  — max results to show (default: 5)
 * @param {Function} [options.onSearch]    — custom search function(query) → Promise<results>
 *
 * @returns {{ query, setQuery, results, isLoading, error, clearResults }}
 */
const useLiveSearch = ({
  debounceMs = 300,
  minLength = 2,
  maxResults = 5,
  onSearch,
} = {}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const cacheRef = useRef({});
  const abortRef = useRef(null);
  const timerRef = useRef(null);

  const defaultSearch = useCallback(async (q) => {
    const params = new URLSearchParams({ keywords: q, perPage: maxResults });
    const res = await fetch(`/api/listings?${params}`);
    if (!res.ok) throw new Error('Search failed');
    const data = await res.json();
    return data.listings || [];
  }, [maxResults]);

  const search = onSearch || defaultSearch;

  useEffect(() => {
    // Clear timer
    clearTimeout(timerRef.current);

    if (!query || query.trim().length < minLength) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    const q = query.trim();

    // Instant cache hit
    if (cacheRef.current[q]) {
      setResults(cacheRef.current[q]);
      return;
    }

    setIsLoading(true);

    timerRef.current = setTimeout(async () => {
      // Cancel previous request
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();

      try {
        const data = await search(q);
        cacheRef.current[q] = data;
        setResults(data);
        setError(null);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message);
          setResults([]);
        }
      } finally {
        setIsLoading(false);
      }
    }, debounceMs);

    return () => clearTimeout(timerRef.current);
  }, [query, debounceMs, minLength, search]);

  const clearResults = useCallback(() => {
    setQuery('');
    setResults([]);
    setError(null);
  }, []);

  return { query, setQuery, results, isLoading, error, clearResults };
};

export default useLiveSearch;
